if (!customElements.get('oldneons-gallery')) {
  customElements.define('oldneons-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.slides = [...this.querySelectorAll('.on-gallery-slide')];
      this.pairCount = Math.ceil(this.slides.length / 2);
      this.index = 0;
      this.toggle = this.querySelector('[data-toggle]');
      this.counter = this.querySelector('[data-current]');
      this.paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.querySelector('[data-next]').addEventListener('click', () => this.move(1), options);
      this.querySelector('[data-previous]').addEventListener('click', () => this.move(-1), options);
      this.toggle.addEventListener('click', () => { this.paused = !this.paused; this.schedule(); }, options);
      this.addEventListener('keydown', event => {
        const image = event.target.closest?.('.on-gallery-slide img');
        if (image && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          this.openImage(image);
          return;
        }
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, options);
      this.slides.forEach(slide => slide.querySelectorAll('img').forEach(image => {
        image.tabIndex = 0;
        image.setAttribute('role', 'button');
        image.setAttribute('aria-label', `Open full-size image: ${image.alt || 'garage photo'}`);
      }));
      this.addEventListener('click', event => {
        const image = event.target.closest?.('.on-gallery-slide img');
        if (image) this.openImage(image);
      }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      document.addEventListener('shopify:block:select', event => {
        const slideIndex = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target));
        if (slideIndex < 0) return;
        this.paused = true;
        this.show(Math.floor(slideIndex / 2));
        this.schedule();
      }, options);
      this.show(0);
      this.schedule();
    }
    show(index) {
      if (!this.pairCount) return;
      this.index = (index + this.pairCount) % this.pairCount;
      const first = this.index * 2;
      this.slides.forEach((slide, i) => { slide.hidden = i < first || i > first + 1; });
      if (this.counter) {
        const last = Math.min(first + 2, this.slides.length);
        this.counter.textContent = first === last - 1 ? String(last) : `${first + 1}–${last}`;
      }
    }
    move(direction) { this.show(this.index + direction); this.schedule(); }
    schedule() {
      clearInterval(this.timer);
      this.toggle.textContent = this.paused ? this.dataset.play : this.dataset.pause;
      this.toggle.setAttribute('aria-pressed', String(this.paused));
      if (!this.paused && !document.hidden && this.pairCount > 1) {
        this.timer = setInterval(() => this.show(this.index + 1), Number(this.dataset.interval) || 4000);
      }
    }
    openImage(sourceImage) {
      this.paused = true;
      this.schedule();
      const imageUrl = sourceImage.currentSrc || sourceImage.src;
      const returnUrl = new URL('/', window.location.origin);
      returnUrl.search = window.location.search;
      returnUrl.hash = '';
      const returnHref = returnUrl.toString().replace(/&/g, '&amp;').replace(/"/g, '&quot;');
      const viewer = window.open('about:blank', '_blank');
      if (!viewer) {
        const fallback = document.createElement('a');
        fallback.href = imageUrl;
        fallback.target = '_blank';
        fallback.rel = 'noopener';
        fallback.click();
        return;
      }
      viewer.opener = null;
      const doc = viewer.document;
      doc.open();
      doc.write(`<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Full-size sign photo</title><style>
        *{box-sizing:border-box}html,body{margin:0;min-height:100%;background:#080c0b;color:#f7faf5;font:15px Arial,Helvetica,sans-serif}
        body{height:100vh;display:flex;flex-direction:column}.toolbar{display:flex;align-items:center;justify-content:center;gap:12px;flex-wrap:wrap;padding:12px;background:#111a14;border-bottom:1px solid #63ff9f;box-shadow:0 0 18px #63ff9f33}
        button{min-height:42px;padding:9px 16px;border:1px solid #63ff9f;border-radius:5px;background:#080c0b;color:#f7faf5;font:inherit;cursor:pointer}
        .toolbar-link{min-height:42px;display:inline-flex;align-items:center;padding:9px 16px;border:1px solid #63ff9f;border-radius:5px;background:#080c0b;color:#f7faf5;text-decoration:none;font:inherit;white-space:nowrap}.toolbar-link:hover{background:#17321f;box-shadow:0 0 12px #63ff9f55}
        button:hover{background:#17321f;box-shadow:0 0 12px #63ff9f55}#zoom-level{min-width:56px;text-align:center;color:#ffe45b;font-weight:700}.zoom-hint{font-size:12px;color:#9eb6a5}
        .photo-title{max-width:min(50vw,620px);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#c9d4cc}.viewport{flex:1;min-height:0;overflow:auto;background:#080c0b}
        #full-size-photo{display:block;max-width:none;height:auto;margin:20px 0;flex:none}
        @media(max-width:600px){.toolbar{gap:7px;padding:9px}button{padding:8px 11px}.photo-title{flex-basis:100%;max-width:100%;text-align:center;order:2}}
      </style></head><body><div class="toolbar"><a id="back-to-main" class="toolbar-link" target="_self" href="${returnHref}">← Back to main page</a><button type="button" id="zoom-out" aria-label="Zoom out">−</button><span id="zoom-level" aria-live="polite">100%</span><button type="button" id="zoom-in" aria-label="Zoom in">+</button><button type="button" id="zoom-reset">Reset</button><span class="zoom-hint">Hold the mouse wheel button + scroll to zoom</span><span class="photo-title" id="photo-title"></span></div><main class="viewport" id="viewport"><img id="full-size-photo" alt=""></main></body></html>`);
      doc.close();
      const photo = doc.getElementById('full-size-photo');
      const viewport = doc.getElementById('viewport');
      const title = sourceImage.alt || 'Garage and neon sign photo';
      doc.title = title;
      doc.getElementById('photo-title').textContent = title;

      photo.alt = title;
      let zoom = 1;
      let baseWidth = Math.min(viewport.clientWidth * 0.9, 1200);
      const updateZoom = () => {
        const width = baseWidth * zoom;
        photo.style.width = width + 'px';
        photo.style.marginLeft = Math.max(0, (viewport.clientWidth - width) / 2) + 'px';
        doc.getElementById('zoom-level').textContent = Math.round(zoom * 100) + '%';
      };
      photo.addEventListener('load', () => {
        baseWidth = Math.min(viewport.clientWidth * 0.9, 1200, photo.naturalWidth || 1200);
        updateZoom();
      });
      photo.src = imageUrl;
      let wheelButtonHeld = false;
      doc.addEventListener('pointerdown', event => {
        if (event.pointerType === 'mouse' && event.button === 1) {
          event.preventDefault();
          wheelButtonHeld = true;
        }
      }, true);
      doc.addEventListener('pointerup', event => {
        if (event.pointerType === 'mouse' && event.button === 1) wheelButtonHeld = false;
      }, true);
      doc.addEventListener('pointercancel', () => { wheelButtonHeld = false; }, true);
      viewer.addEventListener('blur', () => { wheelButtonHeld = false; });
      viewport.addEventListener('wheel', event => {
        if (!wheelButtonHeld || !event.deltaY) return;
        event.preventDefault();
        zoom = Math.min(4, Math.max(0.25, zoom * (event.deltaY < 0 ? 1.15 : 1 / 1.15)));
        updateZoom();
      }, { passive: false });
      doc.getElementById('zoom-in').addEventListener('click', () => { zoom = Math.min(zoom + 0.25, 4); updateZoom(); });
      doc.getElementById('zoom-out').addEventListener('click', () => { zoom = Math.max(zoom - 0.25, 0.25); updateZoom(); });
      doc.getElementById('zoom-reset').addEventListener('click', () => { zoom = 1; updateZoom(); });
      viewer.addEventListener('resize', updateZoom);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.abort?.abort();
      this.ready = false;
    }
  });
}
